import type { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import type { GridMap } from "../generation/GridMap";
import { CELL_SIZE, TileType, type DoorSpawn } from "../generation/types";
import type { MaterialFactory } from "../rendering/MaterialFactory";
import { applyBoxQuadUvs } from "../rendering/UvTiling";
import { gridToWorld } from "../rendering/worldCoordinates";
import type { AudioSystem } from "./AudioSystem";

export enum DoorState {
  Closed = "Closed",
  Opening = "Opening",
  Open = "Open",
  Closing = "Closing",
}

export interface DoorInteractionResult {
  readonly opened: boolean;
  readonly secretDiscovered: boolean;
}

export interface DoorAccessController {
  readonly canOpen: (spawn: DoorSpawn) => boolean;
  readonly onLocked: (spawn: DoorSpawn) => void;
}

const unrestrictedAccess: DoorAccessController = {
  canOpen: () => true,
  onLocked: () => undefined,
};

export class DoorEntity {
  public state = DoorState.Closed;
  public readonly mesh: Mesh;
  private openAmount = 0;
  private openTime = 0;
  private secretDiscovered = false;
  private readonly closedY = 1.8;
  private readonly doorwayHalfSpan: number;

  public constructor(
    public readonly spawn: DoorSpawn,
    scene: Scene,
    private readonly grid: GridMap,
    materials: MaterialFactory,
    private readonly audio: AudioSystem,
    private readonly access: DoorAccessController = unrestrictedAccess,
  ) {
    const world = gridToWorld(spawn.position, grid);
    const doorwaySpan = spawn.cells.length * CELL_SIZE + 0.16;
    this.doorwayHalfSpan = doorwaySpan / 2;
    const dimensions =
      spawn.orientation === "x"
        ? { width: doorwaySpan, height: 3.5, depth: 0.35 }
        : { width: 0.35, height: 3.5, depth: doorwaySpan };
    this.mesh = MeshBuilder.CreateBox(
      `door-${spawn.id}`,
      dimensions,
      scene,
    );
    this.mesh.position.set(world.x, this.closedY, world.z);
    applyBoxQuadUvs(this.mesh, dimensions, spawn.secret ? 4 : doorwaySpan, spawn.secret ? 3.6 : 3.5);
    this.mesh.material = materials.get(
      spawn.secret ? "overgrownBrickWall" : spawn.id.includes("finale") ? "exitDoor" : "door",
    );
    this.mesh.checkCollisions = true;
    this.mesh.isPickable = true;
    this.mesh.metadata = { doorId: spawn.id, surface: "door", uvMapping: "per-quad" };
    if (!spawn.secret) {
      for (const offset of [-doorwaySpan / 2 - 0.14, doorwaySpan / 2 + 0.14]) {
        const frameDimensions = spawn.orientation === "x"
          ? { width: 0.28, height: 3.6, depth: 0.6 }
          : { width: 0.6, height: 3.6, depth: 0.28 };
        const post = MeshBuilder.CreateBox(`door-frame-${spawn.id}-${offset}`, frameDimensions, scene);
        post.position.set(world.x + (spawn.orientation === "x" ? offset : 0), 1.8,
          world.z + (spawn.orientation === "y" ? offset : 0));
        applyBoxQuadUvs(post, frameDimensions);
        post.material = materials.get("brickArchTrim");
        post.metadata = { surface: "wall", doorFrame: spawn.id };
      }
    }
  }

  public interact(): DoorInteractionResult {
    if (this.state === DoorState.Closed || this.state === DoorState.Closing) {
      if (!this.access.canOpen(this.spawn)) {
        this.access.onLocked(this.spawn);
        return { opened: false, secretDiscovered: false };
      }
      this.state = DoorState.Opening;
      this.audio.play("door_open", this.mesh.position);
      const secretDiscovered = this.spawn.secret && !this.secretDiscovered;
      this.secretDiscovered ||= secretDiscovered;
      return { opened: true, secretDiscovered };
    }
    if (this.state === DoorState.Open && !this.spawn.secret) {
      this.state = DoorState.Closing;
      this.audio.play("door_close", this.mesh.position);
    }
    return { opened: false, secretDiscovered: false };
  }

  public update(deltaSeconds: number, occupants: readonly Vector3[]): void {
    if (this.state === DoorState.Opening) {
      this.openAmount = Math.min(1, this.openAmount + deltaSeconds * 1.6);
      this.applyPosition();
      if (this.openAmount >= 0.85) {
        this.mesh.checkCollisions = false;
        this.setFootprint(TileType.Floor);
      }
      if (this.openAmount >= 1) {
        this.state = DoorState.Open;
        this.openTime = 0;
      }
    } else if (this.state === DoorState.Open) {
      this.openTime += deltaSeconds;
      if (this.spawn.autoClose && this.openTime >= 4 && !this.isOccupied(occupants)) {
        this.state = DoorState.Closing;
        this.audio.play("door_close", this.mesh.position);
      }
    } else if (this.state === DoorState.Closing) {
      if (this.isOccupied(occupants)) {
        this.state = DoorState.Opening;
        return;
      }
      this.openAmount = Math.max(0, this.openAmount - deltaSeconds * 1.35);
      this.applyPosition();
      if (this.openAmount <= 0.15) {
        this.mesh.checkCollisions = true;
        this.setFootprint(this.spawn.secret ? TileType.SecretDoor : TileType.Door);
      }
      if (this.openAmount <= 0) {
        this.state = DoorState.Closed;
      }
    }
  }

  private applyPosition(): void {
    this.mesh.position.y = this.closedY + this.openAmount * 3.8;
  }

  private isOccupied(occupants: readonly Vector3[]): boolean {
    return occupants.some((position) => {
      const deltaX = position.x - this.mesh.position.x;
      const deltaZ = position.z - this.mesh.position.z;
      const alongDoor = this.spawn.orientation === "x" ? Math.abs(deltaX) : Math.abs(deltaZ);
      const acrossDoor = this.spawn.orientation === "x" ? Math.abs(deltaZ) : Math.abs(deltaX);
      return alongDoor <= this.doorwayHalfSpan + 0.7 && acrossDoor <= 1.1;
    });
  }

  private setFootprint(tile: TileType): void {
    for (const cell of this.spawn.cells) this.grid.set(cell, tile);
  }
}

export class DoorSystem {
  public readonly doors: readonly DoorEntity[];

  public constructor(
    spawns: readonly DoorSpawn[],
    scene: Scene,
    grid: GridMap,
    materials: MaterialFactory,
    audio: AudioSystem,
    access: DoorAccessController = unrestrictedAccess,
  ) {
    this.doors = spawns.map((spawn) => new DoorEntity(spawn, scene, grid, materials, audio, access));
  }

  public update(deltaSeconds: number, occupants: readonly Vector3[]): void {
    for (const door of this.doors) door.update(deltaSeconds, occupants);
  }

  public nearestUsable(position: Vector3, forward: Vector3, range: number): DoorEntity | undefined {
    let nearest: DoorEntity | undefined;
    let nearestDistance = range;
    for (const door of this.doors) {
      const offsetX = door.mesh.position.x - position.x;
      const offsetY = door.mesh.position.y - position.y;
      const offsetZ = door.mesh.position.z - position.z;
      const distance = Math.hypot(offsetX, offsetY, offsetZ);
      if (distance > nearestDistance || distance <= 0.001) continue;
      const facingDot =
        (offsetX * forward.x + offsetY * forward.y + offsetZ * forward.z) / distance;
      if (facingDot <= 0.35) continue;
      nearest = door;
      nearestDistance = distance;
    }
    return nearest;
  }
}
